import { lazy, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarIcon, CheckIcon, ChevronDownIcon } from "lucide-react";
import type { DateRange } from "react-day-picker";
import { formatDateRange, formatShortDate, type PeriodSummary } from "@scandi/shared";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { RangePreset, RangeSelection } from "@/lib/api";
import type { Translate } from "@/i18n/translate";

// The calendar (react-day-picker and date-fns) is only drawn when somebody
// picks a custom range, so it stays out of every dashboard's first paint.
const Calendar = lazy(() => import("@/components/localized-calendar").then((m) => ({ default: m.LocalizedCalendar })));

/** Rolling presets, defined by how many days back they reach from today. */
const ROLLING: Array<{
  value: RangePreset;
  labelKey: "range.last7d" | "range.last14d" | "range.last30d";
  days: number;
}> = [
  { value: "last_7d", labelKey: "range.last7d", days: 7 },
  { value: "last_14d", labelKey: "range.last14d", days: 14 },
  { value: "last_30d", labelKey: "range.last30d", days: 30 },
];

interface RangePickerProps {
  value: RangeSelection;
  onChange: (range: RangeSelection) => void;
  periods: PeriodSummary[];
  currentPeriodIndex: number | undefined;
  /** Today in the reporting timezone, so presets resolve the way the API will. */
  today: string | undefined;
}

export function RangePicker({
  value,
  onChange,
  periods,
  currentPeriodIndex,
  today,
}: RangePickerProps) {
  const { t } = useTranslation();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>(
    value.preset === "custom" && value.from && value.to
      ? { from: parseDate(value.from), to: parseDate(value.to) }
      : undefined,
  );

  const current = periods.find((p) => p.index === currentPeriodIndex);
  const previous = periods.find((p) => p.index === (currentPeriodIndex ?? 0) - 1);
  // Older periods only; the two most recent already have their own presets.
  const olderPeriods = periods.filter(
    (p) => currentPeriodIndex === undefined || p.index < currentPeriodIndex - 1,
  );

  const periodOptions: Array<{ value: RangePreset; label: string; hint: string | null }> = [
    {
      value: "current_period",
      label: t("range.currentPeriod"),
      // The full span, not the to-date window: this identifies which period you
      // are picking, and matches the range shown in the header.
      hint: current ? spanHint(current.start, current.end) : null,
    },
    {
      value: "previous_period",
      label: t("range.previousPeriod"),
      hint: previous ? spanHint(previous.start, previous.end) : null,
    },
  ];

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="min-w-0 flex-1 justify-between sm:flex-none"
          >
            <span className="truncate">{describe(value, periods, t)}</span>
            <ChevronDownIcon data-icon="inline-end" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuGroup>
            {periodOptions.map((option) => (
              <DropdownMenuItem key={option.value} onSelect={() => onChange({ preset: option.value })}>
                <CheckIcon
                  className={cn(
                    "opacity-0",
                    value.preset === option.value && value.periodIndex === undefined && "opacity-100",
                  )}
                />
                <span className="flex flex-col items-start gap-0.5">
                  {option.label}
                  {option.hint && (
                    <span className="text-muted-foreground text-[11px]">{option.hint}</span>
                  )}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          <DropdownMenuGroup>
            {ROLLING.map((option) => (
              <DropdownMenuItem key={option.value} onSelect={() => onChange({ preset: option.value })}>
                <CheckIcon
                  className={cn(
                    "opacity-0",
                    value.preset === option.value && value.periodIndex === undefined && "opacity-100",
                  )}
                />
                <span className="flex flex-col items-start gap-0.5">
                  {t(option.labelKey)}
                  {today && (
                    <span className="text-muted-foreground text-[11px]">
                      {spanHint(addDays(today, -(option.days - 1)), today)}
                    </span>
                  )}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>

          {olderPeriods.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>{t("range.earlierPeriods")}</DropdownMenuLabel>
              <DropdownMenuGroup>
                {olderPeriods.map((period) => (
                  <DropdownMenuItem
                    key={period.index}
                    onSelect={() => onChange({ preset: "custom", periodIndex: period.index })}
                  >
                    <CheckIcon
                      className={cn("opacity-0", value.periodIndex === period.index && "opacity-100")}
                    />
                    {formatDateRange(period.start, period.end)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={value.preset === "custom" && value.from ? "secondary" : "outline"}
            size="icon-sm"
            aria-label={t("range.pickCustom")}
          >
            <CalendarIcon />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto p-0">
          <Suspense fallback={<div className="h-[330px] w-[288px]" aria-hidden />}>
          <Calendar
            mode="range"
            numberOfMonths={1}
            defaultMonth={draft?.from}
            selected={draft}
            onSelect={setDraft}
            disabled={{ after: new Date() }}
            autoFocus
            className="[--cell-size:--spacing(9)]"
          />
          </Suspense>
          <div className="flex items-center justify-between gap-2 border-t p-3">
            <span className="text-muted-foreground text-xs">
              {draft?.from && draft.to
                ? `${formatDate(draft.from)} → ${formatDate(draft.to)}`
                : t("range.pickPrompt")}
            </span>
            <Button
              size="sm"
              disabled={!draft?.from || !draft.to}
              onClick={() => {
                if (!draft?.from || !draft.to) return;
                onChange({
                  preset: "custom",
                  from: formatDate(draft.from),
                  to: formatDate(draft.to),
                });
                setCalendarOpen(false);
              }}
            >
              {t("common.apply")}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function describe(value: RangeSelection, periods: PeriodSummary[], t: Translate): string {
  if (value.periodIndex !== undefined) {
    const period = periods.find((p) => p.index === value.periodIndex);
    return period ? formatDateRange(period.start, period.end) : t("range.payPeriod");
  }
  if (value.preset === "custom" && value.from && value.to) {
    return formatDateRange(value.from, value.to);
  }
  const rolling = ROLLING.find((p) => p.value === value.preset);
  if (rolling) return t(rolling.labelKey);
  return value.preset === "previous_period"
    ? t("range.previousPeriod")
    : t("range.currentPeriod");
}

/** "26 Jul – 9 Aug", or a single date when the window is one day. */
function spanHint(from: string, to: string): string {
  return from === to ? formatShortDate(from) : formatDateRange(from, to);
}

/**
 * Dates are handled as plain calendar days. Using the local components rather
 * than an ISO conversion avoids the classic off-by-one where a user west of UTC
 * picks the 20th and sends the 19th.
 */
function formatDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function parseDate(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year!, month! - 1, day!);
}

function addDays(date: string, days: number): string {
  const [year, month, day] = date.split("-").map(Number);
  const shifted = new Date(Date.UTC(year!, month! - 1, day! + days));
  return shifted.toISOString().slice(0, 10);
}
